import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import {
  type ChatLog,
  type DuoWorkflowLatestCheckpoint,
  extractUiChatLog,
  GET_LATEST_CHECKPOINT_QUERY,
  WorkflowRunner,
  WorkflowDefinition,
} from '@gitlab-lsp/workflow-api';
import type {
  DuoWorkflowData,
  getWorkflowsVariables,
  DuoMessage,
  DuoWorkflowInfo,
} from '@gitlab-org/graphql';
import { toGitLabGid, tryParseGitLabGidToString } from '@gitlab-org/core';
import type { SessionListItem } from '@gitlab-org/tui';
import type { SessionEvent } from '../backend';
import { ToolInputFormatterService } from '../tool_input_formatter';
import { SessionDataService, type SessionExportSource } from '../../sessions/session_data_service';
import type { GetSessionHistoryOptions, SessionHistoryPage } from '../../sessions/session_history';
import { WorkflowEventMapperService } from './workflow_event_mapper';

// Only these workflow definitions can be resumed by the CLI. workflowDefinition
// is an arbitrary server string, so we allow-list known-runnable flows and drop
// anything else (`duo run` one-shots like `software_development`, remote
// `developer/v2` flows, foundational agents, …) that the CLI cannot resume.
const RESUMABLE_WORKFLOW_DEFINITIONS: ReadonlySet<string> = new Set([
  WorkflowDefinition.CHAT,
  WorkflowDefinition.DEVELOPER_V1,
]);

@Implements(SessionDataService)
@Service({
  dependencies: [Logger, WorkflowRunner, ToolInputFormatterService],
  lifetime: ServiceLifetime.Singleton,
})
export class GitLabSessionDataService implements SessionDataService {
  #logger: Logger;

  #workflowRunner: WorkflowRunner;

  #workflowEventMapper: WorkflowEventMapperService;

  constructor(
    logger: Logger,
    workflowRunner: WorkflowRunner,
    toolInputFormatter: ToolInputFormatterService,
  ) {
    this.#logger = withPrefix(logger, '[SessionDataService]');
    this.#workflowRunner = workflowRunner;
    this.#workflowEventMapper = new WorkflowEventMapperService(toolInputFormatter, logger);
  }

  /**
   * Fetches a paginated list of session history items, filtering out archived and dangling workflows.
   */
  async getSessionHistory(options: GetSessionHistoryOptions): Promise<SessionHistoryPage> {
    const pageSize = options.pageSize ?? 50;
    const isBackward = Boolean(options.beforeCursor);

    const variables: getWorkflowsVariables = {
      // No definition filter: resume reads each session's own definition from the
      // record, so history no longer depends on the current run's flags.
      type: null,
      // Hide web-origin sessions, which share workflow_definition "chat".
      environment: 'IDE',
      first: isBackward ? null : pageSize,
      last: isBackward ? pageSize : null,
      after: options.afterCursor ?? null,
      before: options.beforeCursor ?? null,
      search: options.search ?? null,
    };

    const data = await this.#workflowRunner.getGraphqlData<DuoWorkflowData>({
      operationName: 'duoWorkflows',
      query: null,
      variables,
      signal: options.signal,
    });

    const edges = data.duoWorkflowWorkflows.edges ?? [];
    const items = edges
      .map((edge) => edge.node)
      .filter((workflow) => !workflow.archived)
      .filter((workflow) => this.#isResumableDefinition(workflow))
      .filter((workflow) => !this.#isDanglingPreCreatedWorkflow(workflow))
      .map(
        (workflow) =>
          ({
            id: tryParseGitLabGidToString(workflow.id),
            title: workflow.goal || 'Untitled session',
            status: workflow.humanStatus,
            lastActivity: workflow.updatedAt,
            lastMessagePreview: this.#formatLastMessagePreview(
              workflow.latestCheckpoint?.duoMessages,
            ),
          }) satisfies SessionListItem,
      );

    const { pageInfo } = data.duoWorkflowWorkflows;

    return {
      items,
      pageInfo: {
        hasNextPage: pageInfo.hasNextPage,
        hasPreviousPage: pageInfo.hasPreviousPage,
        endCursor: pageInfo.endCursor || undefined,
        startCursor: pageInfo.startCursor || undefined,
      },
    };
  }

  /**
   * Retrieves session messages as SessionEvents for a given session from its latest checkpoint.
   */
  async getSessionMessages(sessionId: string): Promise<SessionEvent[]> {
    const chatLog = await this.getSessionChatLog(sessionId);
    return this.#workflowEventMapper.mapChatLogToSessionEvents(chatLog);
  }

  async getSessionChatLog(sessionId: string): Promise<ChatLog[]> {
    // An unknown id still throws here, unlike getSessionExportSource.
    const response = await this.#fetchLatestCheckpoint(sessionId);
    return this.#toExportSource(response)?.chatLog ?? [];
  }

  // Undefined for an unknown id; a session with no checkpoint yields an empty chatLog.
  async getSessionExportSource(sessionId: string): Promise<SessionExportSource | undefined> {
    let response: DuoWorkflowLatestCheckpoint;
    try {
      response = await this.#fetchLatestCheckpoint(sessionId);
    } catch (error) {
      if (!this.#isWorkflowNotFound(error)) throw error;
      return undefined;
    }

    return this.#toExportSource(response);
  }

  #fetchLatestCheckpoint(sessionId: string): Promise<DuoWorkflowLatestCheckpoint> {
    return this.#workflowRunner.getGraphqlData<DuoWorkflowLatestCheckpoint>({
      query: GET_LATEST_CHECKPOINT_QUERY,
      variables: { workflowId: toGitLabGid('Ai::DuoWorkflows::Workflow', sessionId) },
    });
  }

  // Message-matched: the rails service rewraps GraphQLError as a plain Error.
  #isWorkflowNotFound(error: unknown): boolean {
    return error instanceof Error && /workflow not found/i.test(error.message);
  }

  #toExportSource(response: DuoWorkflowLatestCheckpoint): SessionExportSource | undefined {
    const node = response.duoWorkflowWorkflows?.nodes?.[0];
    if (!node) {
      return undefined;
    }

    const { latestCheckpoint } = node;
    const source: SessionExportSource = {
      chatLog: [],
      ...(latestCheckpoint?.workflowGoal && { title: latestCheckpoint.workflowGoal }),
      ...(node.workflowDefinition && { flow: node.workflowDefinition }),
    };

    if (!latestCheckpoint) {
      return source;
    }

    const chatLogResult = extractUiChatLog(latestCheckpoint);
    if (chatLogResult.isErr()) {
      this.#logger.error('Failed to extract chat log from checkpoint', chatLogResult.error);
      return source;
    }

    return { ...source, chatLog: chatLogResult.value };
  }

  #formatLastMessagePreview(messages: DuoMessage[] | undefined): string | undefined {
    const last = messages?.at(-1);
    if (!last) return undefined;

    if (last.messageType === 'tool') {
      try {
        const info = JSON.parse(last.toolInfo ?? '{}');
        const name = typeof info.name === 'string' ? info.name : 'unknown';
        return `Used tool: ${name}`;
      } catch {
        return 'Used tool';
      }
    }

    return last.content?.split(/[\r\n]/)[0].trim();
  }

  #isResumableDefinition(workflow: DuoWorkflowInfo): boolean {
    const definition = workflow.workflowDefinition;
    if (definition && RESUMABLE_WORKFLOW_DEFINITIONS.has(definition)) {
      return true;
    }
    this.#logger.info(
      `Filtered session ${tryParseGitLabGidToString(workflow.id)} from the session list because its workflow definition "${definition ?? 'unknown'}" is not resumable by the CLI`,
    );
    return false;
  }

  #isDanglingPreCreatedWorkflow(workflow: DuoWorkflowInfo): boolean {
    const MAX_PRE_CREATED_GOAL_LENGTH = 3;
    return (
      workflow.latestCheckpoint === null &&
      (workflow.goal ?? '').length <= MAX_PRE_CREATED_GOAL_LENGTH
    );
  }
}
